import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, X, Trash2, User as UserIcon, Search, ShieldCheck, Building2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import Portal from "./Portal";

const ROLES = ["Administrator", "Co-Administrator", "General Manager", "Front Office Manager", "Accounts", "Reception", "Sales Manager", "Housekeeping", "Property Owner"];
const PRIVILEGE_GROUPS = {
  Configuration: ["Access to Configuration Panel", "Access to change Language", "Access to create User", "Access to Edit or Delete User", "Access to General Settings", "Access to web settings", "Global Access", "Know your Plan"],
  Reservation: ["Add Reservation", "Edit Reservation", "Cancel Reservation", "Mark NoShow", "Void Reservation", "Modify OTA Booking", "Cancel OTA Booking"],
  "Front Office": ["Check-in", "Check-out", "Move Unit", "Access Guest Details", "Access Folio"],
  Cashiering: ["Add Payment", "Refund Payment", "Void Payment", "Add Charge", "Void Charge"],
  Housekeeping: ["Mark Clean", "Mark Dirty", "Assign HK task"],
  "Special Privileges": ["Access Reports", "Export CSV", "Access CRS", "Access Guest Database", "Access Rates & Availability", "Access Stopsells"],
};
const ALL_PRIVS = Object.values(PRIVILEGE_GROUPS).flat();
// Batch-3 Item 2: role → default privilege template. Selecting a role
// auto-fills these; every checkbox stays manually adjustable afterward so
// individual users can be tuned. Templates were approved by the owner.
const ROLE_TEMPLATES = {
  "Administrator": ALL_PRIVS,
  "Property Owner": ALL_PRIVS,
  "Co-Administrator": ALL_PRIVS.filter((p) => p !== "Access to Edit or Delete User" && p !== "Global Access"),
  "General Manager": [
    ...PRIVILEGE_GROUPS.Reservation, ...PRIVILEGE_GROUPS["Front Office"], ...PRIVILEGE_GROUPS.Cashiering,
    "Mark Clean", "Mark Dirty",
    "Access Reports", "Export CSV", "Access Guest Database", "Access Rates & Availability", "Access Stopsells",
  ],
  "Front Office Manager": [
    ...PRIVILEGE_GROUPS.Reservation, ...PRIVILEGE_GROUPS["Front Office"],
    "Add Payment", "Add Charge",
    "Access Guest Database", "Access Rates & Availability",
  ],
  "Reception": [
    "Add Reservation", "Edit Reservation",
    "Check-in", "Check-out", "Move Unit", "Access Guest Details", "Access Folio",
    "Add Payment",
  ],
  "Sales Manager": [
    ...PRIVILEGE_GROUPS.Reservation,
    "Access Rates & Availability", "Access Stopsells", "Access CRS", "Access Guest Database",
    "Access Reports", "Export CSV",
  ],
  "Accounts": [
    "Access Folio",
    ...PRIVILEGE_GROUPS.Cashiering,
    "Access Reports", "Export CSV",
  ],
  "Housekeeping": ["Mark Clean", "Mark Dirty", "Assign HK task"],
};
const inp = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40";

export default function TeamTab({ pid, propertyName }) {
  const [rows, setRows] = useState(null);
  const [allProps, setAllProps] = useState([]);
  const [q, setQ] = useState("");
  const [showAcross, setShowAcross] = useState(false);
  const [edit, setEdit] = useState(null); // form state
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setRows(null);
    Promise.all([
      api.get("/pms/team", { params: showAcross ? {} : { pid } }),
      api.get("/pms/overview"),
    ]).then(([r, o]) => {
      setRows(r.data.team);
      setAllProps(o.data.properties.filter((p) => p.status === "active"));
    }).catch((e) => toast.error(errMsg(e)));
  }, [pid, showAcross]);
  useEffect(() => { load(); }, [load]);

  const filtered = (rows || []).filter((r) => !q || (r.name + r.email + r.role).toLowerCase().includes(q.toLowerCase()));

  const openNew = () => setEdit({ id: null, name: "", email: "", role: "Reception", short_code: "", property_ids: [pid], privileges: [...(ROLE_TEMPLATES["Reception"] || [])], active: true });
  const openEdit = (r) => setEdit({ ...r, property_ids: r.property_ids || [], privileges: r.privileges || [] });

  const togglePriv = (p) => setEdit((e) => ({ ...e, privileges: e.privileges.includes(p) ? e.privileges.filter((x) => x !== p) : [...e.privileges, p] }));
  const toggleProp = (id) => setEdit((e) => ({ ...e, property_ids: e.property_ids.includes(id) ? e.property_ids.filter((x) => x !== id) : [...e.property_ids, id] }));

  const save = async () => {
    if (!edit.name.trim() || !edit.email.trim() || !edit.role) return toast.error("Name, email, and role are required");
    if (!edit.property_ids.length) return toast.error("Assign at least one property");
    setBusy(true);
    try {
      if (edit.id) {
        await api.patch(`/pms/team/${edit.id}`, edit);
        toast.success(`${edit.name} updated`);
      } else {
        const { data } = await api.post("/pms/team", edit);
        toast.success(data?.invite_delivered
          ? `${edit.name} added · invite email sent to ${edit.email}`
          : `${edit.name} added · share invite link manually (email not delivered)`);
      }
      setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const toggleActive = async (m) => {
    try { await api.patch(`/pms/team/${m.id}`, { active: !m.active }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name}?`)) return;
    try { await api.delete(`/pms/team/${m.id}`); toast.success(`${m.name} removed`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!rows) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-300" size={30} /></div>;

  return (
    <div className="reveal space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div>
            <h2 className="font-display font-bold text-slate-900 text-sm">User Management</h2>
            <p className="text-[11px] text-slate-500 mt-0.5">Add owners, managers, reception & admins. Each user only sees the properties assigned to them — on web and mobile.</p>
          </div>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <label className="text-[11px] text-slate-600 flex items-center gap-1.5"><input type="checkbox" checked={showAcross} onChange={(e) => setShowAcross(e.target.checked)} /> Show all across my portfolio</label>
            <div className="flex items-center gap-2 bg-slate-50 rounded-full px-3 py-1.5 border border-slate-100">
              <Search size={12} className="text-slate-400" />
              <input data-testid="team-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search user…" className="w-40 bg-transparent text-xs focus:outline-none" />
            </div>
            <button data-testid="team-add" onClick={openNew} className="btn-brand rounded-full px-5 py-2 text-xs font-semibold flex items-center gap-2"><Plus size={13} /> Add User</button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 text-[11px] text-slate-500 font-semibold">{filtered.length} of {rows.length} user{rows.length !== 1 ? "s" : ""} · {showAcross ? "across all properties" : propertyName || "this property"}</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
              <th className="text-left px-5 py-3 font-semibold w-16">On/Off</th>
              <th className="text-left px-3 py-3 font-semibold">User Name</th>
              <th className="text-left px-3 py-3 font-semibold">Role</th>
              <th className="text-left px-3 py-3 font-semibold">Email</th>
              <th className="text-left px-3 py-3 font-semibold">Properties</th>
              <th className="text-right px-5 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr key={m.id} data-testid={`team-row-${m.id}`} className="border-b border-slate-50 hover:bg-slate-50/70">
                <td className="px-5 py-3">
                  <button data-testid={`team-toggle-${m.id}`} onClick={() => toggleActive(m)}
                    className={`inline-flex items-center h-6 rounded-full text-[10px] font-bold px-2 gap-1.5 ${m.active ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"}`}>
                    <span className={`w-2 h-2 rounded-full ${m.active ? "bg-white" : "bg-slate-400"}`} />{m.active ? "On" : "Off"}
                  </button>
                </td>
                <td className="px-3 py-3 font-semibold text-slate-800 cursor-pointer" onClick={() => openEdit(m)}>{m.name}{m.short_code && <span className="ml-2 text-[10px] font-mono text-slate-400">{m.short_code}</span>}</td>
                <td className="px-3 py-3 text-slate-600 text-xs">{m.role}</td>
                <td className="px-3 py-3 text-slate-500 text-xs">{m.email}</td>
                <td className="px-3 py-3 text-[11px] text-slate-500">{m.property_ids?.length || 0} · {m.privileges?.length || 0} privilege{(m.privileges?.length || 0) !== 1 ? "s" : ""}</td>
                <td className="px-5 py-3 text-right">
          <button data-testid={`team-invite-btn-${m.id}`} onClick={async () => { try { const { data } = await api.post(`/pms/team/${m.id}/invite`); const full = window.location.origin + data.invite_url; await navigator.clipboard.writeText(full).catch(() => {}); toast.success(data.delivered ? `Invite email sent to ${data.email}` : `Invite link copied to clipboard — share with ${data.email}`); } catch (e) { toast.error(errMsg(e)); } }} className="text-[11px] text-emerald-600 hover:text-emerald-700 font-semibold hover:underline mr-3">Resend Invite</button>
          <button onClick={() => openEdit(m)} className="text-[11px] text-[#4F46E5] font-semibold hover:underline mr-3">Edit</button>
                  <button onClick={() => remove(m)} className="text-slate-300 hover:text-red-500 inline-block"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="6" className="px-6 py-12 text-center text-sm text-slate-400">No users yet — click Add User to invite the first person.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {edit && (
      <Portal>
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[80] grid place-items-center p-4 sm:p-6 overflow-y-auto" onClick={() => setEdit(null)}>
          <div data-testid="team-drawer" className="bg-white w-[96vw] min-h-[92vh] max-h-[92vh] max-w-none rounded-2xl shadow-2xl p-8 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2"><UserIcon size={16} className="brand-pink" /> {edit.id ? "Edit User" : "Add User"}</h3>
              <button data-testid="team-close" onClick={() => setEdit(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-6">
              <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">User Name *</label>
                <input data-testid="team-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className={`${inp} mt-1`} /></div>
              <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Email *</label>
                <input data-testid="team-email" type="email" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} className={`${inp} mt-1`} /></div>
              <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">User Role *</label>
                <select data-testid="team-role" value={edit.role} onChange={(e) => {
                  const newRole = e.target.value;
                  const tpl = ROLE_TEMPLATES[newRole] || [];
                  // Batch-3 Item 2: applying a role fills in that role's
                  // default template. Existing privileges are REPLACED so the
                  // admin sees a clean, predictable baseline they can then
                  // fine-tune. On edit of an existing user we still overwrite
                  // — admins can uncheck anything they don't want post-fill.
                  setEdit({ ...edit, role: newRole, privileges: [...tpl] });
                }} className={`${inp} mt-1`}>{ROLES.map((r) => <option key={r}>{r}</option>)}</select>
                <p className="text-[10px] text-slate-400 mt-1">Selecting a role auto-fills its default privileges — fine-tune below.</p></div>
              <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Short Code</label>
                <input value={edit.short_code} onChange={(e) => setEdit({ ...edit, short_code: e.target.value })} placeholder="e.g. RG" maxLength="4" className={`${inp} mt-1 uppercase font-mono`} /></div>
            </div>

            <div className="mt-6">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><Building2 size={12} /> Property access ({edit.property_ids.length}/{allProps.length})</p>
              <div className="border border-slate-200 rounded-lg p-3 max-h-72 overflow-y-auto space-y-1.5">
                {allProps.map((p) => (
                  <label key={p.id} data-testid={`team-prop-${p.id}`} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 rounded px-2 py-1">
                    <input type="checkbox" checked={edit.property_ids.includes(p.id)} onChange={() => toggleProp(p.id)} />
                    <span className="text-slate-700">{p.name}</span>
                    <span className="ml-auto text-[10px] text-slate-400">{p.city}</span>
                  </label>
                ))}
              </div>
              <p className="text-[10px] text-slate-400 mt-1">On login they'll only see these properties on web + mobile. Admin can switch between them via the property picker.</p>
            </div>

            <div className="mt-6">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><ShieldCheck size={12} /> Privileges ({edit.privileges.length})</p>
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                {Object.entries(PRIVILEGE_GROUPS).map(([group, items]) => {
                  const selectedInGroup = items.filter((i) => edit.privileges.includes(i)).length;
                  const allOn = selectedInGroup === items.length;
                  return (
                    <div key={group} className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-slate-700 text-xs">{group} <span className="text-slate-400 font-normal">({selectedInGroup}/{items.length})</span></span>
                        <button onClick={() => setEdit((e) => ({ ...e, privileges: allOn ? e.privileges.filter((x) => !items.includes(x)) : [...new Set([...e.privileges, ...items])] }))} className="text-[10px] text-[#4F46E5] font-semibold hover:underline">{allOn ? "Clear group" : "Select all"}</button>
                      </div>
                      <div className="grid grid-cols-2 gap-1.5">
                        {items.map((i) => (
                          <label key={i} className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                            <input type="checkbox" checked={edit.privileges.includes(i)} onChange={() => togglePriv(i)} /> {i}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 flex items-center gap-2">
              <label className="text-xs text-slate-600 flex items-center gap-1.5"><input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} /> Active — allow this user to sign in</label>
              <button data-testid="team-save" onClick={save} disabled={busy}
                className="btn-brand ml-auto rounded-full px-6 py-2.5 text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
                {busy ? <Loader2 className="animate-spin" size={14} /> : null} {edit.id ? "Save changes" : "Add user"}
              </button>
            </div>
          </div>
        </div>
      </Portal>
      )}
    </div>
  );
}
